import { isNullOrUndefined } from '@deltasierra/type-utilities';
import { TextLayer, TextFit } from '../layers/textLayer';
import { IRenderContext } from '../../graphics/renderContext';
import { convertUnits, POINTS_PER_INCH, PrintUnits } from '../print/printUnits';
import { findMatchingFontMetricsOrDefault } from '../../graphics/text';
import { getFallbackFont } from '../../utils/fontUtils';
import { IRenderTextOptions } from './common';

export class TextRendererV1 {
    // eslint-disable-next-line max-statements
    public renderText(options: IRenderTextOptions): void {
        const { context, customFonts, layer } = options;

        context.fillColor(layer.fill);
        context.strokeColor(layer.stroke);

        (context as any).context.textBaseline = layer.textBaseline;
        (context as any).context.textAlign = layer.textAlign;
        (context as any).context.direction = layer.direction;

        context.lineWidth(layer.strokeWidth);
        const textData = this.processText(options);
        const lines = textData.lines;
        const fontSize = textData.fontSize;
        const lineHeight = textData.lineHeight;
        context.fontFamily(layer.fontFamily).fontSize(fontSize, layer.fontSizeUnit);
        const calcSize = this.calculateTextDimensions(context, lines, lineHeight);

        let x: number;
        let y: number;
        if (!layer.textVerticalAlign || layer.textVerticalAlign === 'top') {
            y = 0;
        } else if (layer.textVerticalAlign === 'center') {
            y = Math.floor(layer.height / 2 - calcSize.height / 2);
        } else if (layer.textVerticalAlign === 'bottom') {
            y = Math.floor(layer.height - calcSize.height);
        } else {
            y = 0; // Just to make sure it's not undefined
        }

        let yOffset = 0;
        if (options.useTextRenderingLineHeightFix) {
            const fallbackFontFamily = getFallbackFont(
                options.layer.text,
                options.layer.fontFamily,
                options.textToFallbackFontLink,
            );
            // DS-2925: The improved fix for the line heights
            const metrics = findMatchingFontMetricsOrDefault(layer.fontFamily, customFonts, fallbackFontFamily);
            const fontSizeInPt = convertUnits(layer.fontSize, layer.fontSizeUnit as PrintUnits, PrintUnits.pixels);
            const resolution = 72;
            yOffset = (metrics.ascent * fontSizeInPt * resolution) / (POINTS_PER_INCH * metrics.unitsPerEm);
        } else if (options.useOldTextRenderingLineHeightFix) {
            /*
             * DS-2647: Text rendering fix. This fix is mainly targeted at print templates using TextRenderingV2
             * But is needed here for old templates.
             */
            yOffset = lineHeight / 2;
        }

        if (layer.clip) {
            context.beginPath();
            context.rect({ height: layer.height, width: layer.width, x: 0, y: 0 });
            context.clip();
        }

        for (const line of lines) {
            const lineWidth = context.textWidth(line);

            x = this.getLineXPosition(layer, lineWidth);

            context.text({
                fill: !isNullOrUndefined(layer.fill),
                position: { x, y: y + yOffset },
                stroke: !isNullOrUndefined(layer.stroke) && layer.strokeWidth > 0,
                text: line,
            });
            y += lineHeight;
        }
    }

    private applyTextEffects(layer: TextLayer, text: string): string {
        let newText = text;
        if (layer.uppercase) {
            newText = text.toUpperCase();
        }
        return newText;
    }

    private wrapText(lines: string[], context: IRenderContext, maxWidth: number) {
        const newLines = [];
        for (const originalLine of lines) {
            const words = originalLine.split(' ');
            let line = '';
            let testLine = '';
            // eslint-disable-next-line id-length
            for (let j = 0; j < words.length; j++) {
                const word = words[j];
                testLine = `${line + word} `;
                const width = context.textWidth(testLine);
                if (width > maxWidth && j > 0) {
                    newLines.push(line);
                    line = `${word} `;
                } else {
                    line = testLine;
                }
            }
            newLines.push(line);
        }
        return newLines;
    }

    private calculateTextDimensions(context: IRenderContext, lines: string[], lineHeight: number) {
        let width = 0;
        for (const line of lines) {
            width = Math.max(context.textWidth(line), width);
        }
        const height = lines.length * lineHeight;

        return {
            height,
            width,
        };
    }

    private processText(options: IRenderTextOptions) {
        const { context, layer, substituteText } = options;

        let text = substituteText(layer.text);
        text = this.applyTextEffects(layer, text);
        const maxWidth = layer.width;
        const maxHeight = layer.height;
        let lines = text.split('\n');
        let fontSize = layer.fontSize ? layer.fontSize : 32;
        const lineHeight = fontSize * layer.lineHeight;

        context.fontFamily(layer.fontFamily).fontSize(fontSize, layer.fontSizeUnit);

        const textFit = (layer as any).textFit;

        if (!textFit || textFit === TextFit.wordWrap) {
            // If property is missing, default to word wrap.
            lines = this.wrapText(lines, context, maxWidth);
        } else if (textFit === TextFit.shrinkToBounds) {
            const calcSize = this.calculateTextDimensions(context, lines, lineHeight);
            const widthRatio = maxWidth / calcSize.width;
            const heightRatio = maxHeight / calcSize.height;
            const minRatio = Math.min(widthRatio, heightRatio, 1);
            if (minRatio < 1) {
                fontSize *= minRatio;
            }
        }

        return {
            fontSize,
            lineHeight: fontSize * layer.lineHeight,
            lines,
        };
    }

    private getLineXPosition(layer: TextLayer, lineWidth: number): number {
        switch (layer.textAlign) {
            case 'left':
                return 0;

            case 'center':
                return Math.floor(layer.width / 2 - lineWidth / 2);

            case 'right':
                return layer.width - lineWidth;

            default:
                return 0;
        }
    }
}
